import { Box, Button, Grid, Stack, styled } from "@mui/material";
import React from "react";
import {
  CheckboxComponent,
  DateField,
  SelectFieldComponent,
} from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import GenericForm from "../external/ensolvers-core-frontend-mui/components/Common/GenericForm";
import { Documentation } from "../models/Documentation";
import Timeoff from "../models/Timeoff";
import CustomDateFnsUtils from "../utils/CustomDateFnsUtils";
import { TimeOffType, timeoffOptions } from "../types/TimeOffType";
import TimeOffDocumentationList from "./TimeOffDocumentationList";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import { SpinnerWrapper } from "./SpinnerWrapper";
import { useStyles } from "./Main/formStyles";
import { Token } from "../theme";

interface Props {
  userExternalId: string;
  selectedFiles: Documentation[];
  onClose: () => void;
  onSave: (instance: Timeoff) => void;
  handleUploadDocument: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteDocument: (files: Documentation[]) => void;
  isLoading: boolean;
  usernames: AutoCompleteUIDTO[];
  open: boolean;
  forOthers: boolean;
}

const handleUsers = (users: AutoCompleteUIDTO[], userExternalId: string) => {
  let newUsers = users.reduce((acc: AutoCompleteUIDTO[], curr) => {
    if (!acc.find((item) => item.externalId === curr.externalId)) acc.push({ externalId: curr.externalId, label: curr.label });
    return acc;
  }, []);

  return newUsers.map((u) => ({ label: u.label, value: u.externalId })).filter((item) => item.value !== userExternalId);
};

export default function TimeoffCreateDialog(props: Props) {
  const customStyles = useStyles();

  return (
    <GenericForm<Timeoff>
      headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
      buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
      cancelButtonStyles={{
        color: Token.textSecondary,
        border: `1px solid ${Token.borderDefault}`,
        marginBottom: 10,
      }}
      dialogStyles={customStyles.root}
      dialog={true}
      open={!!props.open}
      title={"Time Off Request"}
      onSave={(instance) => {
        setTimeout(() => {
          props.onClose();
        }, 500);
        props.onSave(instance);
      }}
      onCancel={() => props.onClose()}
      dialogCustomSubmitMessage={"Save"}
      resetValue={{ requesterExternalId: "", type: "", shortStartDate: null, shortEndDate: null, halfDay: null }}
      renderContent={(register, errors, control) => (
        <Box>
          <SpinnerWrapper isLoading={props.isLoading}>
            {props.forOthers && (
              <>
                <SelectFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"requesterExternalId"}
                  label={"Member"}
                  required={true}
                  options={handleUsers(props.usernames, props.userExternalId)}
                />
              </>
            )}

            <SelectFieldComponent
              control={control}
              register={register}
              errors={errors}
              propertyName={"type"}
              label={"Time Off Type"}
              required={true}
              options={timeoffOptions}
            />
            <DateField
              control={control}
              register={register}
              errors={errors}
              propertyName={"shortStartDate"}
              label="Start Date"
              required={true}
              utils={CustomDateFnsUtils}
            />
            <DateField
              control={control}
              register={register}
              errors={errors}
              propertyName={"shortEndDate"}
              label="End Date"
              required={true}
              utils={CustomDateFnsUtils}
            />
            <CheckboxComponent
              control={control}
              register={register}
              errors={errors}
              propertyName={"halfDay"}
              label="Half day"
              required={false}
            />
            <Grid>
              <TimeOffDocumentationList
                viewTimeoff={undefined}
                selectedFiles={props.selectedFiles}
                onDelete={(files: Documentation[]) => props.handleDeleteDocument(files)}
              />
              <Stack direction="row">
                <label htmlFor="contained-button-file">
                  <Input
                    id="contained-button-file"
                    type="file"
                    accept=".pdf"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.handleUploadDocument(e)}
                  />
                  <SpinnerWrapper isLoading={props.isLoading}>
                    <Button variant="contained" component="span" sx={{ margin: ".7em" }}>
                      Add document
                    </Button>
                  </SpinnerWrapper>
                </label>
              </Stack>
            </Grid>
          </SpinnerWrapper>
        </Box>
      )}
    />
  );
}

const Input = styled("input")({
  display: "none",
});
